/** Searchable asset selector used by create dialogs. */
import { useQuery } from "@tanstack/react-query";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "@/api/client";
import { unwrap } from "@/api/problem";
import { Input, Select } from "@/shared/ui/form";

/** Props for `AssetPicker`. */
export interface AssetPickerProps {
  id: string;
  value: string;
  onChange: (assetId: string) => void;
  /** Whether a selection is mandatory (default true). */
  required?: boolean;
}

/** Type-ahead search box plus select of matching assets. */
export function AssetPicker({
  id,
  value,
  onChange,
  required = true,
}: AssetPickerProps): ReactElement {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const query = useQuery({
    queryKey: ["assets", "picker", q],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/assets/", { params: { query: { q: q || undefined, limit: 50 } } }),
      ).items,
    staleTime: 60_000,
  });
  const options = query.data ?? [];
  return (
    <div className="space-y-2">
      <Input
        aria-label={t("app.search")}
        placeholder={t("app.search")}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <Select id={id} value={value} onChange={(e) => onChange(e.target.value)} required={required}>
        <option value="">—</option>
        {options.map((a) => (
          <option key={a.id} value={a.id}>
            {a.code} · {a.name}
          </option>
        ))}
      </Select>
    </div>
  );
}
